import { type Permission } from '@up2stay/up2promo-api-client';
import { useCallback, useMemo } from 'react';

import { useMyMemberships } from '@/core/hooks/use-my-memberships';
import { usePermissionsQuery } from '@/core/hooks/use-permissions-query';
import { useCurrentWorkspace } from '@/core/store/workspace.store';

export type PermissionCheck = Permission | string;

interface UsePermissionsResult {
  permissions: Set<Permission>;
  can: (permission: PermissionCheck) => boolean;
  canAny: (permissions: PermissionCheck[]) => boolean;
  canAll: (permissions: PermissionCheck[]) => boolean;
  isLoading: boolean;
}

/**
 * Resolves the current workspace's effective permissions from the backend.
 * Use this (not role) for all authorization decisions in the UI.
 */
export function usePermissions(): UsePermissionsResult {
  const currentWorkspace = useCurrentWorkspace();
  const { memberships, isLoading: membershipsLoading } = useMyMemberships();
  const permissionsQuery = usePermissionsQuery(currentWorkspace?.id);

  const membershipPermissions = useMemo(() => {
    if (!currentWorkspace) return undefined;
    const membership = memberships.find(
      (entry) => entry.workspace?.id === currentWorkspace.id,
    );
    return membership?.permissions;
  }, [currentWorkspace, memberships]);

  const permissions = useMemo(() => {
    const source =
      permissionsQuery.data ?? membershipPermissions ?? ([] as Permission[]);
    return new Set(source);
  }, [membershipPermissions, permissionsQuery.data]);

  const isLoading =
    !currentWorkspace ||
    membershipsLoading ||
    (permissionsQuery.isPending && membershipPermissions === undefined);

  const can = useCallback(
    (permission: PermissionCheck) => {
      if (isLoading) return false;
      return permissions.has(permission as Permission);
    },
    [isLoading, permissions],
  );

  const canAny = useCallback(
    (required: PermissionCheck[]) => {
      if (isLoading) return false;
      return required.some((permission) =>
        permissions.has(permission as Permission),
      );
    },
    [isLoading, permissions],
  );

  const canAll = useCallback(
    (required: PermissionCheck[]) => {
      if (isLoading) return false;
      return required.every((permission) =>
        permissions.has(permission as Permission),
      );
    },
    [isLoading, permissions],
  );

  return {
    permissions,
    can,
    canAny,
    canAll,
    isLoading,
  };
}
